import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useDispatch } from 'react-redux';
import { CustomSelect } from 'src/components';
import Button from 'src/components/button';
import FormikDatepicker from 'src/components/datepicker/FormikDatepicker';
import SlideInModal from 'src/components/slideInModal';
import TextField from 'src/components/text-field';
import { fetchExpenses } from 'src/redux/action/expenses.action';
import Service from 'src/setup/network';
import { toastAlert } from 'src/utils/toastAlert';
import * as Yup from 'yup';
import PaymentSection from '../Payments';

interface IProps {
  handleModal: () => void;
  showModal: boolean;
  setIsEdit: (val: boolean) => void;
  setEditId: (val: string) => void;
  generateRandomDigits: (e: any) => void;
  isEdit: boolean;
  editId: string;
  token?: string;
  editExpensesData: any;
  qrData: {
    id: string;
    name: string;
  }[];
  filters: any;
}

type expensesPostValue = {
  date: Date;
  name: string;
  quantity: string;
  amount: string;
  payments: any[];
  note: string;
  recipientName: string;
  localVendorId: string;
};

const FORM_VALIDATION = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Full Name must a at least 3 character long')
    .required('Full Name is required'),
  quantity: Yup.string()
    .typeError('Please enter quantity')
    .required('Quantity is required'),
  amount: Yup.string()
    .typeError('Please enter amount')
    .required('Amount is required'),
  note: Yup.string().typeError('Note must be in string'),
  payments: Yup.array()
  .of(
    Yup.object().shape({
      amount: Yup.number()
        .typeError('Enter a number')
        .positive('Must be positive')
        .required('Required'),
      date: Yup.date().required('Required'),
      methodId: Yup.string().required('Required'),
    })
  )
});

const AddExpenses: React.FC<IProps> = ({
  showModal,
  handleModal,
  editId,
  isEdit,
  setEditId,
  setIsEdit,
  editExpensesData,
  qrData,
  filters,
}) => {
  const dispatch = useDispatch();
  const [errorMsgPost, setErrorMsgPost] = React.useState('');

  const intValues = {
    date: isEdit ? new Date(editExpensesData?.date) : new Date(),
    name: isEdit ? editExpensesData?.name : '',
    quantity: isEdit ? editExpensesData?.quantity : '',
    amount: isEdit ? editExpensesData.amount : '',
    note: isEdit ? editExpensesData.note : '',
    recipientName: isEdit ? editExpensesData.recipientName : '',
    localVendorId: isEdit ? editExpensesData.localVendorId : '',
    payments: isEdit ?
      editExpensesData.payments.map((p: any) => ({
        amount: p.amount || '',
        date: p.date ? new Date(p.date) : new Date(),
        methodId: p.method?.id || '', 
      }))
      : []
      };


  const handleSubmit = async (val: expensesPostValue) => {
    try {
      const payload = {
        date: val.date.toISOString(),
        name: val.name,
        quantity: val.quantity,
        amount: val.amount,
        note: val.note,
        recipientName: val.recipientName,
        localVendorId: val.localVendorId || '',
        payments: val.payments.map((p: any) => ({
          amount: Number(p.amount),
          date: new Date(p.date).toISOString(),
          methodId: p.methodId,
        })),
      };
      if (editId) {
        await Service.put(`/expenses/${editId}`, payload);
      } else {
        await Service.post('/expenses', payload);
      }
      handleModal();
      toastAlert(
        'success',
        `Expenses  ${val?.name} is successfully ${editId ? 'edited' : 'added'}`
      );
      dispatch(fetchExpenses({ ...filters, to: new Date() }) as any);
      setIsEdit(false);
      setEditId('');
    } catch (err: any) {
      setErrorMsgPost(err?.response?.data?.message);
      setIsEdit(false);
      setEditId('');
    }
  };

  return (
    <>
      {showModal && (
        <SlideInModal
          showModal={showModal}
          title={isEdit ? 'Edit Expenses' : 'Add Expenses'}
          onClick={handleModal}
          className="h-[550px] overflow-y-auto w-[500px] bg-pure-white z-[9999]"
        >
          <div className="mx-5 mt-4">
            {errorMsgPost && !isEdit && (
              <p className="text-warning text-center text-sm font-semibold capitalize">
                {errorMsgPost}
              </p>
            )}
            <Formik
              initialValues={intValues}
              validateOnMount
              validationSchema={FORM_VALIDATION}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, isValid, setFieldValue }) => (
                <Form className="mt-5 pb-16">
                  <FormikDatepicker
                    name="date"
                    label="Date & Time"
                    showTime={true}
                  />
                  <TextField label="Name" name="name" />
                  <TextField label="Quantity" name="quantity" />
                  <TextField label="Amount" name="amount" />
                  {/* <Field
                    name="paymentMethodId"
                    className="mt-3"
                    options={qrData}
                    component={CustomSelect}
                    isMulti={false}
                    label="Payment Method"
                    defaultValue={
                      isEdit ? editExpensesData.paymentMethod.name : undefined
                    }
                  /> */}
                  <PaymentSection qrData={qrData} isEdit={isEdit} />
                  <label
                    htmlFor="note"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Note
                  </label>
                  <Field
                    as="textarea"
                    name="note"
                    rows={4}
                    className="mt-1 mb-4 w-full appearance-none text-text-black placeholder:capitalize rounded-xl py-3 block placeholder:text-sm px-3 bg-pure-white text-sm ring-1 ring-light-grey focus-within:ring-1 focus-within:ring-primary focus-within:outline-0"
                  />
                  <TextField
                    label="Recipient Name"
                    name="recipientName"
                    type="text"
                  />
                  <div className="flex items-center justify-end mt-10">
                    <Button
                      text={isEdit ? 'Edit Expenses' : 'Add Expenses'}
                      variant="primary"
                      type="submit"
                      isSubmitting={isSubmitting}
                      isValid={isValid}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </SlideInModal>
      )}
    </>
  );
};

export default React.memo(AddExpenses);
